/**
 * Vuex Getters for Notification Module
 * Computed properties that derive state from the notification store
 * Provide filtered, sorted, and computed data for components
 */

/**
 * Get all notifications from state
 * Basic state accessor without transformation
 * @param {Object} state - Notification module state
 * @returns {Array} All notifications in current state
 */
export function notifications(state) {
  return state.notifications;
}

/**
 * Get current unread notification count
 * Used for badge counters and UI indicators
 * @param {Object} state - Notification module state
 * @returns {number} Count of unread notifications
 */
export function unreadCount(state) {
  return state.unreadCount;
}

/**
 * Get main loading state
 * Indicates if initial notifications are being loaded
 * @param {Object} state - Notification module state
 * @returns {boolean} Loading state flag
 */
export function loading(state) {
  return state.loading;
}

/**
 * Get pagination loading state
 * Indicates if additional notifications are being loaded
 * @param {Object} state - Notification module state
 * @returns {boolean} Loading more state flag
 */
export function loadingMore(state) {
  return state.loadingMore;
}

/**
 * Get "mark all as read" operation state
 * Indicates if bulk read operation is in progress
 * @param {Object} state - Notification module state
 * @returns {boolean} Marking all as read state flag
 */
export function markingAllAsRead(state) {
  return state.markingAllAsRead;
}

/**
 * Get pagination availability state
 * Indicates if more notifications are available to load
 * @param {Object} state - Notification module state
 * @returns {boolean} Has more notifications flag
 */
export function hasMore(state) {
  return state.hasMore;
}

/**
 * Get current notification filter
 * Returns the active filter type for notification display
 * @param {Object} state - Notification module state
 * @returns {string} Current filter: 'all' or 'unread'
 */
export function filter(state) {
  return state.filter;
}

/**
 * Get filtered notifications based on current filter
 * Applies active filter to notification list
 * @param {Object} state - Notification module state
 * @returns {Array} Filtered notifications array
 */
export function filteredNotifications(state) {
  switch (state.filter) {
    case "unread":
      return state.notifications.filter((n) => !n.read);
    default:
      return state.notifications;
  }
}
